import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AppBar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Button,
  TextField,
  Divider,
  ListItemIcon,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { Logout } from "@mui/icons-material";
import { IoSearch } from "react-icons/io5";
import { MdOutlineLightMode } from "react-icons/md";
import { FaBell, FaLocationDot } from "react-icons/fa6";
import { GoSignOut } from "react-icons/go";

// Import images (ensure these paths are correct)
import logo from "../Assets/logo.png";
import farmhouse from "./image/farmhouse.png";
import coupon from "./image/coupon.png";
import favorites from "./image/favorites.png";
import booking from "./image/booking.png";
import eventicon from "./image/event (1).png";
import hearticon from "./image/heart.png";
import MyFavorites from "./image/MyFavorites.png";
import History from "./image/History.png";
import Signout from "./image/Signout.png";
import WhatsAppicon from "./image/WhatsAppicon.png.webp";

import "./Navbar.css";
import { useModal } from "../context/context";
import Loginpopup from "./Loginmodal";
import LocationModal from "./LocationModal";
import SearchbarMobile from "../Homepage/Searchbar-mobile";
import FooterAppDownloadModal from "../Feed Page/custom-comp/FooterAppDownloadModal";

// Constants
const API_BASE_URL = "https://partywitty.com/master";
const ASSETS_URL = `${API_BASE_URL}/assets/uploads/`;

// Styled Components
const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 280,
    color: theme.palette.mode === "light" ? "rgb(55, 65, 81)" : theme.palette.grey[300],
    boxShadow: "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  border: "0.5px solid #fff",
  borderRadius: "20px",
  boxShadow: 24,
  p: 3,
  overflow: "auto",
};

// Utility Functions
const validateEmail = (email) => {
  const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return emailPattern.test(email);
};

const checkLoginExpiry = () => {
  const loginData = localStorage.getItem("userData");
  const timestamp = localStorage.getItem("loginTimestamp");
  
  if (loginData && timestamp) {
    const loginTime = new Date(timestamp);
    const currentTime = new Date();
    const timeDifference = currentTime - loginTime;
    const timeLimit = 24 * 60 * 60 * 1000; // 24 hours
    
    if (timeDifference > timeLimit) {
      localStorage.removeItem("userData");
      localStorage.removeItem("loginTimestamp");
      return null;
    }
    return JSON.parse(loginData);
  }
  return null;
};

// Custom Hooks
const useUserData = () => {
  const [userData, setUserData] = useState(null);
  
  useEffect(() => {
    const data = checkLoginExpiry();
    setUserData(data);
  }, []);
  
  return userData;
};

const useGeolocation = () => {
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
        },
        (error) => console.error("Error getting location:", error)
      );
    }
  }, []);
  
  return location;
};

// Main Component
export default function Navbar() {
  // State Management
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [profileUpdateModal, setProfileUpdateModal] = useState(false);
  const [imageUpdateModal, setImageUpdateModal] = useState(false);
  const [appDownloadModal, setAppDownloadModal] = useState(false);
  const [locationModal, setLocationModal] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [userLocation, setUserLocation] = useState("Select Location");
  const [searchInput, setSearchInput] = useState("");
  
  // Profile Update States
  const [updateData, setUpdateData] = useState({
    name: "",
    email: "",
    dob: null,
    gender: "male",
    mobile: "",
  });
  const [profileImage, setProfileImage] = useState(null);
  
  // Hooks
  const userData = localStorage.getItem("userData") ? JSON.parse(localStorage.getItem("userData")) : null;
  const { toggle } = useModal();
  const location = useGeolocation();
  const navigate = useNavigate();
  const currentLocation = useLocation();
  
  // Navigation Items
  const navItems = [
    {
      label: 'Home',
      path: '/feed',
      matchPaths: ['/', '/feed'],
    },
    {
      label: 'My Plan',
      path: '/my-plan',
      matchPaths: ['/my-plan', '/table-book-form', '/package-lead-form'],
    },
  ];
  
  // Mobile Menu Items
  const mobileMenuItems = [
    { label: "Home", path: "/feed" },
    { label: "My Plan", path: "/my-plan" },
    ...(userData ? [
      { label: "My Booking", path: "/my-booking-history", icon: eventicon },
      { label: "My Favorites", path: "/favourite-club", icon: hearticon },
    ] : []),
    { label: "About Us", path: "/about-us" },
  ];
  
  // Effects
  useEffect(() => {
    if (toggle) {
      setShowLoginPopup(true);
    }
  }, [toggle]);
  
  // Event Handlers
  const handleAccountMenuOpen = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };
  
  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };
  
  const handleLoginRequired = () => {
    navigate("/login");
  };
  
  const handleSearch = () => {
    const query = searchInput.trim() || 'fly';
    navigate(`/result?${query}`);
  };
  
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };
  
  const handleUpdateProfile = async () => {
    if (!validateEmail(updateData.email)) {
      alert("Please enter a valid email address");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("user_id", userData.id);
      Object.entries(updateData).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      
      const response = await axios.post(
        `${API_BASE_URL}/Api/UserAuth/UpdateProfile`,
        formData
      );
      
      if (response.data.status) {
        localStorage.setItem("userData", JSON.stringify(response.data.data));
        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };
  
  const handleUpdateProfileImage = async () => {
    if (!profileImage) return;
    
    try {
      const formData = new FormData();
      formData.append("user_id", userData.id);
      formData.append("img", profileImage);
      
      const response = await axios.post(
        `${API_BASE_URL}/Api/UserAuth/updateProfileImage`,
        formData
      );
      
      if (response.data.status) {
        localStorage.setItem("userData", JSON.stringify(response.data.data));
        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating profile image:", error);
    }
  };
  
  // Render Mobile Drawer
  const renderMobileDrawer = () => (
    <Box sx={{ width: 250, height: "100%", backgroundColor: "white" }}>
      <List>
        {/* User Profile Section */}
        <ListItem>
          <ListItemText
            primary={
              userData ? (
                <div className="my-account--profile-name">
                  <div>
                    <img src={ASSETS_URL + userData.img} alt="user" />
                  </div>
                  <div>
                    <p className="my-account--profile-name--1">
                      {userData.name?.split(' ')[0]}
                    </p>
                    <p className="my-account--profile-name--1--2">User Profile</p>
                  </div>
                </div>
              ) : (
                <button
                  className="signup-button2"
                  onClick={handleLoginRequired}
                >
                  Login/SignUp
                </button>
              )
            }
          />
        </ListItem>
        
        {/* Menu Items */}
        {mobileMenuItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton>
              <Link
                to={item.path}
                style={{ textDecoration: "none", color: "black", width: "100%" }}
              >
                <div className="ham--profile">
                  {item.icon && <img src={item.icon} alt={item.label} />}
                  <p className="m-0">{item.label}</p>
                </div>
              </Link>
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Download App */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setAppDownloadModal(true)}>
            <div className="ham--profile">
              <p className="m-0">Download App</p>
            </div>
          </ListItemButton>
        </ListItem>
      </List>
      
      <Divider />
      
      {/* Logout Section */}
      {userData && (
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <div className="ham--profile1">
                <img src={Signout} alt="signout" />
                <p className="m-0">Sign Out</p>
              </div>
            </ListItemButton>
          </ListItem>
        </List>
      )}
      
      {/* Help Center */}
      <List>
        <ListItem>
          <div className="my-account--profile-name1">
            <div>
              <img src={WhatsAppicon} alt="help" />
            </div>
            <div>
              <p className="my-account--profile-name--1">Help Center</p>
              <p className="my-account--profile-name--1--2">Answer Here</p>
            </div>
          </div>
        </ListItem>
      </List>
    </Box>
  );
  
  return (
    <div className="home-page-design">
      {showLoginPopup && <Loginpopup />}
      
      <div className="header1">
        <div className="header">
          <div className="header-navbar">
            <div className="nav-logo">
              <Link to="/" style={{ textDecoration: "none", marginLeft: "0px" }}>
                <img src={logo} alt="logo" />
              </Link>
              <div className="nav-select-location">
                <div onClick={() => setLocationModal(true)}>
                  <FaLocationDot />{" "}
                  {userLocation.length > 20 
                    ? userLocation.slice(0, 20) + "..." 
                    : userLocation
                  }
                </div>
              </div>
            </div>
          </div>
          
          <div className="navbar">
            <div className="navbar-menu">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`menu-nav-links ${
                    item.matchPaths.includes(currentLocation.pathname) ? 'active' : ''
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            
            <div className="navbar-icons">
              <div className="nav-darkmode-icons">
                <IoSearch onClick={handleSearch} size={20} />
              </div>
              <div className="nav-darkmode-icons">
                <MdOutlineLightMode size={25} />
              </div>
              <div className="nav-darkmode-icons">
                <FaBell size={23} />
              </div>
            </div>
            
            <div className="menuu">
              {userData ? (
                <div className="profile_icon" onClick={handleAccountMenuOpen}>
                  <img src={ASSETS_URL + userData.img} alt="profile" />
                </div>
              ) : (
                <button className="signup-button2" onClick={handleLoginRequired}>
                  Login/SignUp
                </button>
              )}
            </div>
          </div>
          
          <div className="navbar--hamburger--sign">
            <IoSearch onClick={handleSearch} />
            <div
              className="menu-toggle"
              onClick={() => setDrawerOpen(true)}
            >
              <div className="bar"></div>
              <div className="bar"></div>
              <div className="bar"></div>
            </div>
          </div>
        </div>
        
        {/* Mobile Search Bar */}
        <div className="mobile--view--search-bar">
          <SearchbarMobile />
        </div>
      </div>
      
      {/* Account Menu */}
      <StyledMenu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
      >
        {userData && (
          <>
            <MenuItem onClick={handleAccountMenuClose} disableRipple>
              <div className="my-account--payment">
                <div className="my-account--profile-name">
                  <div>
                    <img src={ASSETS_URL + userData.img} alt="profile" />
                  </div>
                  <div>
                    <p className="my-account--profile-name--1">{userData.name}</p>
                    <p className="my-account--profile-name--1--2">User Profile</p>
                  </div>
                </div>
                <ul className="no-bullets">
                  <li>
                    <Link to="/my-booking-history" style={{ textDecoration: "none", color: "black" }}>
                      <div className="my-account--payment--wallet">
                        <img src={History} alt="history" />
                        <p>My Booking</p>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link to="/favourite-club" style={{ textDecoration: "none", color: "black" }}>
                      <div className="my-account--payment--wallet">
                        <img src={MyFavorites} alt="favorites" />
                        <p>My Favorites</p>
                      </div>
                    </Link>
                  </li>
                </ul>
              </div>
            </MenuItem>
            
            <MenuItem onClick={handleLogout}>
              <div className="my--account--sign-out">
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <p>Sign out</p>
              </div>
            </MenuItem>
            
            <MenuItem onClick={handleAccountMenuClose}>
              <div className="my-account--profile-name1">
                <div>
                  <img src={WhatsAppicon} alt="help" />
                </div>
                <div>
                  <p className="my-account--profile-name--1">Help Center</p>
                  <p className="my-account--profile-name--1--2">Answer Here</p>
                </div>
              </div>
            </MenuItem>
          </>
        )}
      </StyledMenu>
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {renderMobileDrawer()}
      </Drawer>
      
      {/* Location Modal */}
      {locationModal && (
        <LocationModal
          isOpen={locationModal}
          onClose={() => setLocationModal(false)}
          onSelectLocation={(location) => {
            setUserLocation(location);
            setLocationModal(false);
          }}
        />
      )}
      
      {/* Profile Update Modal */}
      <Modal
        open={profileUpdateModal}
        onClose={() => setProfileUpdateModal(false)}
      >
        <Box sx={ModalStyle}>
          <div className="login-content1">
            <h3>Update Profile</h3>
            <Box sx={{ "& > :not(style)": { m: 1 } }}>
              <TextField
                fullWidth
                label="Name"
                value={updateData.name}
                onChange={(e) => setUpdateData({ ...updateData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={updateData.email}
                onChange={(e) => setUpdateData({ ...updateData, email: e.target.value })}
              />
              <Button
                variant="contained"
                onClick={handleUpdateProfile}
                fullWidth
                sx={{ mt: 2 }}
              >
                Update Profile
              </Button>
            </Box>
          </div>
          <Button
            onClick={() => setProfileUpdateModal(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            ×
          </Button>
        </Box>
      </Modal>
      
      {/* Image Update Modal */}
      <Modal
        open={imageUpdateModal}
        onClose={() => setImageUpdateModal(false)}
      >
        <Box sx={ModalStyle}>
          <div className="login-content1">
            <h3>Update Profile Image</h3>
            <Box sx={{ "& > :not(style)": { m: 1 } }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setProfileImage(e.target.files[0])}
              />
              <Button
                variant="contained"
                onClick={handleUpdateProfileImage}
                fullWidth
                sx={{ mt: 2 }}
              >
                Update Image
              </Button>
            </Box>
          </div>
          <Button
            onClick={() => setImageUpdateModal(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            ×
          </Button>
        </Box>
      </Modal>
      
      {/* App Download Modal */}
      <FooterAppDownloadModal
        isOpen={appDownloadModal}
        onClose={() => setAppDownloadModal(false)}
      />
    </div>
  );
}